import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Truck, Zap } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-white"
    >
      {/* Background grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #0A0A0A 1px, transparent 1px), linear-gradient(to bottom, #0A0A0A 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative px-6 md:px-12 lg:px-24 pt-12 md:pt-20 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center min-h-[78vh]">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            {/* Eyebrow */}
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 border border-[#1A1A1A]/15 bg-white px-3 py-1.5"
            >
              <span className="w-1.5 h-1.5 bg-[#5BB247] dot-pulse" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1A1A1A]">
                Now onboarding Luton van drivers · UK
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black text-[#0A0A0A] leading-[0.95]"
            >
              House moves &amp; clearance,
              <br />
              <span className="relative inline-block">
                <span className="relative z-10">paid the right way.</span>
                <span
                  aria-hidden="true"
                  className="absolute left-0 bottom-1 right-0 h-3 bg-[#F5A623]/60 -z-0"
                />
              </span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-6 max-w-xl text-lg text-[#475569] leading-relaxed"
            >
              Raanah connects customers with verified Luton van drivers for house
              removals, waste clearance, and office moves — payment held in
              Stripe escrow, released on proof of completion. WhatsApp alerts.
              Weekly payouts. Zero chase-ups.
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="#waitlist"
                data-testid="hero-cta-poster"
                className="group inline-flex items-center justify-between gap-3 bg-[#1E4D55] text-white px-7 py-4 font-semibold hover:bg-[#163C42] transition-colors"
              >
                <span>Book a move</span>
                <ArrowUpRight
                  size={18}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
              <a
                href="#waitlist"
                data-testid="hero-cta-driver"
                className="group inline-flex items-center justify-between gap-3 bg-transparent border border-[#0A0A0A] text-[#0A0A0A] px-7 py-4 font-semibold hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                <span>Drive &amp; earn</span>
                <ArrowUpRight
                  size={18}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
            </div>

            {/* Trust badges */}
            <div
              data-testid="hero-trust-row"
              className="mt-12 grid grid-cols-3 max-w-lg divide-x divide-[#0A0A0A]/10 border-t border-[#0A0A0A]/10 pt-6"
            >
              <div className="pr-4">
                <ShieldCheck size={18} className="text-[#1E4D55]" />
                <p className="mt-2 text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">
                  Escrow secured
                </p>
                <p className="text-xs text-[#475569]">Stripe Connect</p>
              </div>
              <div className="px-4">
                <Truck size={18} className="text-[#1E4D55]" />
                <p className="mt-2 text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">
                  DVLA verified
                </p>
                <p className="text-xs text-[#475569]">Drivers &amp; vans</p>
              </div>
              <div className="pl-4">
                <Zap size={18} className="text-[#1E4D55]" />
                <p className="mt-2 text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">
                  Weekly payouts
                </p>
                <p className="text-xs text-[#475569]">Every Friday</p>
              </div>
            </div>
          </motion.div>

          {/* Right visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-[#0A0A0A]/10">
              <img
                src="https://images.pexels.com/photos/7464398/pexels-photo-7464398.jpeg"
                alt="White Luton-style moving van parked on a city street"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              {/* Overlay info card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="absolute left-4 bottom-4 right-4 md:left-6 md:bottom-6 md:right-6 bg-white border border-[#0A0A0A]/10 p-5"
                data-testid="hero-floating-card"
              >
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] font-bold text-[#475569]">
                  <span>Job #RNH-4821</span>
                  <span className="text-[#5BB247] flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5BB247] dot-pulse" />
                    In escrow
                  </span>
                </div>
                <p className="mt-3 font-display text-2xl font-bold text-[#0A0A0A] leading-tight">
                  Leeds → Manchester · 3-bed removal
                </p>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider font-bold text-[#475569]">
                      Accepted quote
                    </p>
                    <p className="font-display text-3xl font-black text-[#0A0A0A] tracking-tight">
                      £485
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-[#475569]">
                      ETA
                    </p>
                    <p className="font-semibold text-[#0A0A0A]">Today · 14:30</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Small accent block */}
            <div className="hidden md:block absolute -top-4 -left-4 w-20 h-20 bg-[#1E4D55]" />
          </motion.div>
        </div>
      </div>

      {/* Logo marquee */}
      <div className="border-y border-[#0A0A0A]/10 bg-white overflow-hidden">
        <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center gap-8">
          <span className="shrink-0 text-[11px] uppercase tracking-[0.22em] font-bold text-[#475569]">
            Built with
          </span>
          <div className="overflow-hidden w-full">
            <div className="flex animate-marquee gap-16 whitespace-nowrap text-[#0A0A0A]/70 font-display font-bold tracking-tight text-lg">
              {[
                "Stripe Connect",
                "WhatsApp Cloud API",
                "DVLA verification",
                "Twilio",
                "What3Words",
                "Mapbox",
                "SendGrid",
                "Cloudflare",
              ]
                .concat([
                  "Stripe Connect",
                  "WhatsApp Cloud API",
                  "DVLA verification",
                  "Twilio",
                  "What3Words",
                  "Mapbox",
                  "SendGrid",
                  "Cloudflare",
                ])
                .map((b, i) => (
                  <span key={i} className="flex items-center gap-3">
                    <span className="w-1 h-1 bg-[#0A0A0A]/40" />
                    {b}
                  </span>
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
